import {
  agGridDataFormatter,
  shouldCellRenderAsNumber,
  shouldRenderAsHtml,
} from './helpers/TableColumnFormatting';
import { getFieldName } from './helpers/getFieldName';
import { type ICellRendererParams } from 'ag-grid-community';
import { type CustomAgGridContext } from 'common/types/agGrid/context';
import { type ViewColumn } from 'common/types/viewColumn';
import { CustomLinkRenderer } from './CustomLinkRenderer';
import {
  defaultLinkedAction,
  defaultTableActionsContext,
  resolveMappedColumnsForRow,
} from './helpers/tableActionsHelper';
import { type TableActionsContextField } from './TableActionsButton/types';
import { isAggregateGroupCell, isNormalCell } from './helpers/isTotalCell';
import { type CustomLabels } from 'common/visualizations/vif';
import { getTruncatedGeometry } from 'common/util/truncated-geometry';
import { NULL_GROUPING } from './Constants';

export interface IValueRendererProps extends ICellRendererParams<any, any, Partial<CustomAgGridContext>> {
  domain: string;
  datasetUid: string;
  wrapWithDivElement: boolean;
  forExport?: boolean;
  customLabels?: CustomLabels;
}

export const ValueRenderer = (params: IValueRendererProps) => {
  const ctx = params.context;
  const columnMetadata = ctx.columnMetadata ?? [];
  const columnFormats = ctx.columnFormats ?? {};
  const nonStandardAggregations = ctx.nonStandardAggregations ?? [];
  const linkedAction = ctx.linkedAction ?? defaultLinkedAction;
  const tableActionsContext = ctx.tableActionsContext ?? defaultTableActionsContext;
  const fieldName = getFieldName(params);

  const renderTextAsHtml = (text: string) => {
    return <div dangerouslySetInnerHTML={{ __html: text }}></div>;
  };

  const formattedValue = agGridDataFormatter(
    params.domain,
    params.datasetUid,
    columnMetadata,
    nonStandardAggregations,
    params,
    columnFormats,
    false,
    false,
    params.customLabels,
  );

  // Currently, we do not need to export values enclosed in div elements in the Excel export.
  if (params.forExport) {
    return formattedValue;
  }

  // A full WKT geometry is typically too long to read in a cell, and can cause browser performance issues
  // Export, copy and the tooltip all format the value without this renderer's display path,
  // so they still get the full WKT.
  const displayValue = getCellTruncatedValue(params, columnMetadata) ?? formattedValue;

  // Total and subtotal rows show aggregated values, not row values, action links must not render on them
  if (
    isNormalCell(params) &&
    !isAggregateGroupCell(params) &&
    linkedAction?.selectedExistingColumn &&
    fieldName === linkedAction.selectedExistingColumn
  ) {
    const { mappedColumns } = tableActionsContext;
    const selectedColumns = mappedColumns?.map(
      (mappedColumn: TableActionsContextField) => mappedColumn.selectedColumn,
    );

    if (!mappedColumns || !selectedColumns || selectedColumns.length === 0) {
      return displayValue;
    }

    const resolved = resolveMappedColumnsForRow(params.node, params.data, mappedColumns);

    if (resolved.ambiguous || resolved.missing) {
      return displayValue;
    }

    const selectedActionForExistingColumn = linkedAction.selectedActionForExistingColumn;

    return (
      <CustomLinkRenderer
        formattedValue={displayValue}
        tableActionsContext={{ ...tableActionsContext, mappedColumns: resolved.columns }}
        selectedActionForExistingColumn={selectedActionForExistingColumn}
      />
    );
  }

  return params.wrapWithDivElement || shouldRenderAsHtml(params.node, columnMetadata)
    ? renderTextAsHtml(displayValue)
    : displayValue;
};

// returns null for cells that don't have or need truncation
function getCellTruncatedValue(params: IValueRendererProps, columnMetadata: ViewColumn[]) {
  const hasAValue = params.value && params.value !== NULL_GROUPING;

  // Totals and aggregates of a geometry column (like a count) aren't geometry values
  const isAggregate =
    !isNormalCell(params) || isAggregateGroupCell(params) || shouldCellRenderAsNumber(params);

  if (!hasAValue || isAggregate) {
    return null;
  }

  const fieldName = getFieldName(params);
  const column = columnMetadata.find((c) => c.fieldName === fieldName);

  return getTruncatedGeometry(column?.dataTypeName);
}
